import React from "react";
import { PivotTableData, PivotConfig } from "../types";
import { Table } from "../../../../../../ui/components/Table";

interface PivotPreviewTableProps {
  config: PivotConfig;
  data: PivotTableData | null;
}

export const PivotPreviewTable: React.FC<PivotPreviewTableProps> = ({
  config,
  data,
}) => {
  // Generate mock preview data based on config
  const generateMockPreview = (): PivotTableData => {
    if (config.rowGroups.length === 0 && config.values.length === 0) {
      return {
        rows: [],
        columns: [],
        data: [],
      };
    }

    // Simple mock data generation
    const rowHeaders = config.rowGroups.map((f) => f.name);
    const colHeaders = config.columnLabels.map((f) => f.name);
    const valueFields = config.values.map((f) => f.name);

    // Generate sample rows
    const sampleRows = [
      ["Apollo Hospitals Enterprise Ltd", "Domestic"],
      ["Hexaware Technologies Ltd", "Domestic"],
      ["NEULAND LABORATORIES LIMITED", "Domestic"],
    ];

    // Generate sample columns
    const sampleCols = colHeaders.length > 0 
      ? ["Jan-2025", "Feb-2025", "Mar-2025"]
      : valueFields;

    // Generate sample data
    const sampleData = sampleRows.map(() =>
      sampleCols.map(() => Math.floor(Math.random() * 1000000))
    );

    return {
      rows: sampleRows,
      columns: sampleCols,
      data: sampleData,
    };
  };

  const previewData = data || generateMockPreview();

  if (previewData.rows.length === 0) {
    return (
      <div className="p-8 text-center border border-neutral-border rounded-md bg-default-background">
        <p className="text-body font-body text-subtext-color">
          Configure pivot fields to see preview
        </p>
      </div>
    );
  }

  // Build table structure
  const tableRows = previewData.rows.map((row, rowIndex) => {
    const rowData = [...row, ...previewData.data[rowIndex]];
    return rowData;
  });

  const allHeaders = [
    ...config.rowGroups.map((f) => f.name),
    ...previewData.columns,
  ];

  return (
    <div className="border border-neutral-border rounded-md bg-default-background overflow-auto">
      <div className="p-4 border-b border-neutral-border">
        <h3 className="text-heading-3 font-heading-3 text-default-font">
          Pivot Preview
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-neutral-50 border-b border-neutral-border">
              {allHeaders.map((header, idx) => (
                <th
                  key={idx}
                  className="px-4 py-2 text-left text-body-bold font-body-bold text-default-font border-r border-neutral-border last:border-r-0"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                className="border-b border-neutral-border hover:bg-neutral-50"
              >
                {row.map((cell, cellIdx) => (
                  <td
                    key={cellIdx}
                    className="px-4 py-2 text-body font-body text-default-font border-r border-neutral-border last:border-r-0"
                  >
                    {typeof cell === "number"
                      ? cell.toLocaleString("en-IN")
                      : cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
